import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  Download,
  Phone,
  User
} from "lucide-react";
import adminApi from "../../../api/adminApi";
import "../../../assets/css/admin/appointments.css";

const AppointmentsManagement = () => {
  const location = useLocation();

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("Appointments"); // Appointments | Completed | Cancelled
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortBy, setSortBy] = useState("Newest");
  const [searchTerm, setSearchTerm] = useState(location.state?.search || "");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const tabToApiTab = { Appointments: "upcoming", Completed: "completed", Cancelled: "cancelled" };

  // ── Fetch appointments from backend (read-only) ────────────────
  const fetchAppointments = useCallback(() => {
    setLoading(true);
    adminApi
      .get(`/appointments`, {
        params: {
          tab: tabToApiTab[activeTab],
          status: filterStatus,
          search: searchTerm || undefined,
        },
      })
      .then((res) => {
        setAppointments(res.data.data || []);
      })
      .catch((err) => {
        console.error(err);
        Swal.fire({ icon: "error", title: "Failed", text: "Could not load appointments." });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterStatus, searchTerm]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Coming from the header's global search — apply the term it passed along
  useEffect(() => {
    if (location.state?.search) {
      setSearchTerm(location.state.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const stats = {
    total: appointments.length,
    upcoming: appointments.filter((a) => a.status === "Confirmed" || a.status === "Pending").length,
    completed: appointments.filter((a) => a.status === "Completed").length,
    cancelled: appointments.filter((a) => a.status === "Cancelled" || a.status === "Rejected").length,
  };

  // Client-side sort only (filtering/search/tab already handled by API)
  const sortedAppointments = [...appointments].sort((a, b) => {
    if (sortBy === "Patient Name") return a.patient.name.localeCompare(b.patient.name);
    if (sortBy === "Oldest") return a.id - b.id;
    return b.id - a.id; // Newest
  });

  const totalPages = Math.ceil(sortedAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAppointments = sortedAppointments.slice(startIndex, startIndex + itemsPerPage);

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
  };

  const handleExportCSV = () => {
    Swal.fire({
      title: "Export Data",
      text: "CSV file will be downloaded shortly",
      icon: "info",
      confirmButtonColor: "#1e3a5f",
      timer: 2000,
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="am-wrapper">
      <div className="am-container">
        {/* Header */}
        <div className="am-header">
          <div>
            <h1 className="am-title">Appointments Management</h1>
            <p className="am-subtitle">Manage and track all appointments</p>
          </div>
          <button className="am-btn-export" onClick={handleExportCSV}>
            <Download size={18} />
            Export CSV
          </button>
        </div>

        {/* Stats Cards */}
        <div className="am-stats-grid">
          <div className="am-stat-card">
            <div className="am-stat-icon am-stat-blue">
              <Calendar size={24} />
            </div>
            <div>
              <div className="am-stat-value">{stats.total}</div>
              <div className="am-stat-label">Total Appointments</div>
            </div>
          </div>
          <div className="am-stat-card">
            <div className="am-stat-icon am-stat-orange">
              <Clock size={24} />
            </div>
            <div>
              <div className="am-stat-value">{stats.upcoming}</div>
              <div className="am-stat-label">Upcoming</div>
            </div>
          </div>
          <div className="am-stat-card">
            <div className="am-stat-icon am-stat-green">
              <CheckCircle size={24} />
            </div>
            <div>
              <div className="am-stat-value">{stats.completed}</div>
              <div className="am-stat-label">Completed</div>
            </div>
          </div>
          <div className="am-stat-card">
            <div className="am-stat-icon am-stat-red">
              <XCircle size={24} />
            </div>
            <div>
              <div className="am-stat-value">{stats.cancelled}</div>
              <div className="am-stat-label">Cancelled</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="am-main-card">
          {/* Tabs */}
          <div className="am-tabs">
            {["Appointments", "Completed", "Cancelled"].map((tab) => (
              <button
                key={tab}
                className={`am-tab ${activeTab === tab ? "am-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="am-filters">
            <span className="am-filter-label">Filter by:</span>
            <select
              className="am-filter-select"
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All</option>
              <option>Confirmed</option>
              <option>Pending</option>
            </select>
            <select className="am-filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option>Newest</option>
              <option>Oldest</option>
              <option>Patient Name</option>
            </select>
            <div className="am-search-wrapper">
              <Search size={18} className="am-search-icon" />
              <input
                type="text"
                placeholder="Search"
                className="am-search-input"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Content Grid */}
          <div className="am-content-grid">
            {/* Left: Appointments List */}
            <div className="am-list-section">
              <h3 className="am-section-title">
                {activeTab === "Appointments" ? "Upcoming Appointments" : activeTab}
              </h3>

              {loading ? (
                <div className="am-empty-state">
                  <Calendar size={48} />
                  <p>Loading appointments...</p>
                </div>
              ) : sortedAppointments.length === 0 ? (
                <div className="am-empty-state">
                  <Calendar size={48} />
                  <p>No appointments found</p>
                </div>
              ) : (
                <>
                  <div className="am-table-container">
                    <table className="am-table">
                      <thead>
                        <tr>
                          <th className="am-th">Appointment ID</th>
                          <th className="am-th">Patient</th>
                          <th className="am-th">Therapist</th>
                          <th className="am-th">Date &amp; Time</th>
                          <th className="am-th">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedAppointments.map((appointment, index) => (
                          <tr key={appointment.id} className="am-tr" style={{ animationDelay: `${index * 0.05}s` }}>
                            <td className="am-td">
                              <span className="am-appointment-id">{appointment.invoice_id}</span>
                            </td>
                            <td className="am-td">
                              <div className="am-patient-info">
                                <div>
                                  <div className="am-patient-name">{appointment.patient.name}</div>
                                  <div className="am-patient-age">{appointment.patient.age}</div>
                                </div>
                              </div>
                            </td>
                            <td className="am-td">
                              <div className="am-therapist-name">{appointment.therapist.name}</div>
                              <div className="am-therapist-spec">{appointment.therapist.specialization}</div>
                            </td>
                            <td className="am-td">
                              <div className="am-date">{appointment.dateTime.date}</div>
                              <div className="am-time">{appointment.dateTime.time}</div>
                            </td>
                            <td className="am-td">
                              {/* Admin can only VIEW — reschedule/cancel removed on purpose.
                                  Those actions belong to the therapist (accept/reject) and
                                  the patient (cancel) only. */}
                              <button className="am-btn-view" onClick={() => handleViewDetails(appointment)}>
                                View Details →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="am-pagination">
                    <div className="am-pagination-info">
                      Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedAppointments.length)} of{" "}
                      {sortedAppointments.length} appointments
                    </div>
                    <div className="am-pagination-controls">
                      <button
                        className="am-pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                          return (
                            <button
                              key={page}
                              className={`am-pagination-number ${currentPage === page ? "am-pagination-number-active" : ""}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return (
                            <span key={page} style={{ padding: "0 8px", color: "#94a3b8" }}>
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}

                      <button
                        className="am-pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right: Details Panel - NO BUTTONS & NO IMAGES */}
            <div className="am-details-panel">
              {selectedAppointment ? (
                <div className="am-details-content">
                  <h2 className="am-details-title">Appointment Summary</h2>

                  <div className="am-summary-card">
                    <div className="am-summary-info">
                      <h3 className="am-summary-name">{selectedAppointment.patient.name}</h3>
                      <p className="am-summary-with">With: {selectedAppointment.therapist.name}</p>
                    </div>
                  </div>

                  <div className="am-detail-box">
                    <div className="am-detail-row">
                      <Calendar size={16} className="am-detail-icon" />
                      <span>
                        {selectedAppointment.dateTime.date} {selectedAppointment.dateTime.time}
                      </span>
                    </div>
                    <div className="am-detail-row">
                      <span className="am-detail-id">{selectedAppointment.invoice_id}</span>
                    </div>
                  </div>

                  <div className="am-status-row">
                    <span className={`am-status-badge am-status-${selectedAppointment.status.toLowerCase()}`}>
                      {selectedAppointment.status}
                    </span>
                    <span className="am-rating-badge">
                      <Users size={14} />
                      {selectedAppointment.session_type}
                    </span>
                  </div>

                  <div className="am-summary-divider">
                    <h3 className="am-summary-subtitle">Patient Info</h3>
                  </div>

                  <div className="am-contact-card">
                    <div className="am-contact-info">
                      <p className="am-contact-name">{selectedAppointment.patient.name}</p>
                      <p className="am-contact-email">{selectedAppointment.patient.email}</p>
                      <p className="am-contact-phone">
                        <Phone size={14} />
                        {selectedAppointment.patient.phone}
                      </p>
                    </div>
                  </div>

                  <div className="am-info-section">
                    <h3 className="am-section-heading">Therapist Info</h3>
                    <div className="am-info-box">
                      <div className="am-info-header">
                        <User size={16} className="am-info-icon" />
                        <span className="am-info-label">Therapist Info</span>
                      </div>
                      <p className="am-info-text">{selectedAppointment.therapist.name}</p>
                      <p className="am-info-text">Specialization: {selectedAppointment.therapist.specialization}</p>
                      <p className="am-info-text">Experience: {selectedAppointment.therapist.experience}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="am-no-selection">
                  <Calendar size={48} />
                  <p>Select an appointment to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsManagement;